import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postDataDeletionConfirm } from '../../queries/data-deletion';
import sxStyles from './styles';

type Status = 'idle' | 'loading' | 'success' | 'expiredToken' | 'error';

type ConfirmDeletionViewProps = {
  token: string;
};

export default function ConfirmDeletionView({ token }: ConfirmDeletionViewProps) {
  const [status, setStatus] = React.useState<Status>('idle');
  const theme = useTheme();
  const handleConfirm = async () => {
    setStatus('loading');
    try {
      await postDataDeletionConfirm(token);
      setStatus('success');
    } catch (e) {
      if (e === 401) {
        setStatus('expiredToken');
      } else {
        setStatus('error');
      }
    }
  };

  if (status === 'success') {
    return (
      <Box textAlign="center">
        <Alert severity="success" sx={sxStyles.alert}>
          Tus datos han sido eliminados exitosamente.
        </Alert>
      </Box>
    );
  }

  if (status === 'expiredToken') {
    return (
      <Box textAlign="center">
        <Alert
          severity="error"
          sx={{
            ...sxStyles.alert,
            backgroundColor: theme.palette.grey[300],
            color: 'text.primary',
            fontWeight: 'bold',
          }}
        >
          El link de confirmación expiró. Solicita uno nuevo.
        </Alert>
        <Link href="/user-data-deletion" underline="hover" sx={{ display: 'block', mt: 2 }}>
          Volver a solicitar eliminación
        </Link>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box textAlign="center" sx={{ mt: 5 }}>
        <Alert
          severity="error"
          sx={{
            ...sxStyles.alert,
            backgroundColor: theme.palette.grey[300],
            color: 'text.primary',
            fontWeight: 'bold',
          }}
        >
          Ha ocurrido un error, por favor contáctanos.
          <Typography
            variant="body2"
            color="primary"
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 0.5,
            }}
          >
            <Link href="mailto:soporte@banca.me" underline="hover">
              soporte@banca.me
            </Link>
          </Typography>
        </Alert>
      </Box>
    );
  }
  return (
    <Box textAlign="center">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        sx={{ mb: 2, marginTop: 2 }}
      >
        <WarningAmberIcon color="warning" fontSize="small" sx={{ color: theme.palette.error.main }} />
        <Typography variant="body1" color="primary">
          ¡Esta acción es irreversible!
        </Typography>
      </Box>
      <Alert sx={{
        textAlign: 'left', marginBlock: 5, backgroundColor: theme.palette.grey[100],
      }}
      >
        Tu cuenta y todos tus datos serán eliminados permanentemente.
      </Alert>
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        disabled={status === 'loading'}
        onClick={handleConfirm}
      >
        {status === 'loading'
          ? <CircularProgress size={22} color="inherit" />
          : 'Confirmar eliminación'}
      </Button>
    </Box>
  );
}
